import { Vector3 } from 'three';

import type { Skeleton } from '../skeleton/core';
import {
  CHAIN_DEFINITIONS,
  ChainMapping,
  getFingerChains,
  isContainerLikeName,
} from '../schema/mapping';
import { getChainRoleAssignments } from '../mapping/chainRoleAssignments';
import type { ScoreBreakdown } from './scoring';

import { type ChainResult, MappingResult, RecognizerConfig } from './config';
import { detectRoot } from './phases/root';
import type { DetectedPreset } from './rigPresets';
import { detectedPresetChainMappings, detectedPresetName } from './rigPresets';
import {
  type AugmentationContext,
  type DetectedFeature,
  runAllAugmentations,
} from './augmentations';
import { suggestAccessoryBranchExclusions } from './helpers';

const HELPER_TOKENS = [
  'part', 'twist', 'roll', 'adjust', 'correction', 'corrective',
  'bend', 'aux', 'helper', 'sub', 'between',
];

function isHelperBoneName(name: string): boolean {
  const lower = name.toLowerCase();
  return HELPER_TOKENS.some((tok) => lower.includes(tok));
}

function findJointBone(
  skel: Skeleton,
  chainBones: string[],
  jointHints: string[],
  minBendAngle = 15.0,
): string {
  const candidates =
    chainBones.length >= 3
      ? chainBones.slice(1, -1)
      : chainBones.slice(1, 2);

  if (candidates.length === 0) {
    return chainBones.length > 1 ? chainBones[1] : chainBones[0];
  }

  for (const boneRef of candidates) {
    const idx = skel.resolveBoneRef(boneRef, { namespace: 'internal' });
    if (idx === null) continue;
    const nameLower = skel.bone(idx).name.toLowerCase();
    if (jointHints.some((hint) => nameLower.includes(hint))) return boneRef;
  }

  let bestBone: string | null = null;
  let bestAngle = 0.0;

  for (const boneRef of candidates) {
    const idx = skel.resolveBoneRef(boneRef, { namespace: 'internal' });
    if (idx === null) continue;

    const bone = skel.bone(idx);
    if (bone.parent === null) continue;

    const parent = skel.bone(bone.parent);
    const boneDir = bone.direction;
    const parentDir = parent.direction;

    const normBone = boneDir.length();
    const normParent = parentDir.length();
    if (normBone < 1e-8 || normParent < 1e-8) continue;

    const dot = boneDir.clone().divideScalar(normBone).dot(parentDir.clone().divideScalar(normParent));
    const angle = (Math.acos(Math.max(-1.0, Math.min(1.0, dot))) * 180) / Math.PI;

    if (angle > bestAngle) {
      bestAngle = angle;
      bestBone = boneRef;
    }
  }

  if (bestBone !== null && bestAngle >= minBendAngle) return bestBone;
  return chainBones.length > 1 ? chainBones[1] : chainBones[0];
}

function makeBreakdown(total: number): ScoreBreakdown {
  return {
    topology: 1.0,
    symmetry: 1.0,
    direction: 1.0,
    lengthRatio: 1.0,
    depth: 1.0,
    naming: 1.0,
    total,
  };
}

function maybeAddRootFromPresetPelvis(
  skel: Skeleton,
  chainMappings: Record<string, [number, number, string | null]>,
): void {
  if ('Root' in chainMappings) return;

  const pelvisMapping = chainMappings['Pelvis'];
  if (!pelvisMapping) return;

  const [pelvisIdx] = pelvisMapping;
  const rootResult = detectRoot(skel, pelvisIdx, new RecognizerConfig(), null);
  let rootIdx = rootResult?.[0] ?? null;
  if (rootIdx === null) {
    const nonContainerAncestors = skel
      .ancestors(pelvisIdx)
      .filter((idx) => !skel.bone(idx).auxiliary || idx === skel.root)
      .filter((idx) => !isContainerLikeName(skel.bone(idx).name));
    if (nonContainerAncestors.length === 1) {
      rootIdx = nonContainerAncestors[0];
    }
  }

  if (rootIdx === null) return;
  if (rootIdx === pelvisIdx) return;

  chainMappings['Root'] = [rootIdx, rootIdx, null];
}

export function buildResultFromRigPreset(
  skel: Skeleton,
  preset: DetectedPreset,
  excludedBones: string[] = [],
): MappingResult {
  const rig = preset.preset;

  const presetBoneNames = new Set<string>();
  for (const b of rig.signatureBones) presetBoneNames.add(b);
  for (const b of rig.optionalBones) presetBoneNames.add(b);

  const unmarkedBones: string[] = [];
  for (const bone of skel.bones) {
    if (!bone.auxiliary) continue;
    let boneBase = bone.name;
    if (preset.prefix) {
      boneBase = bone.name.replace(preset.prefix, '');
    }
    if (presetBoneNames.has(boneBase) && !isHelperBoneName(bone.name)) {
      bone.auxiliary = false;
      unmarkedBones.push(bone.name);
    }
  }

  const excluded = excludedBones.filter((b) => !unmarkedBones.includes(b));

  const roleToBone: Record<string, string> = {};
  const roleToConfidence: Record<string, number> = {};
  const explain: Record<string, ScoreBreakdown> = {};
  const warnings: string[] = [];
  const chains: Record<string, string[]> = {};
  const chainResults: Record<string, ChainResult> = {};

  const baseConfidence = Math.min(1.0, 0.85 + preset.preset.confidenceBoost);
  const chainMappings = detectedPresetChainMappings(preset, skel);

  // If the preset defines a Root chain and the bone wasn't found by name,
  // try matching the skeleton root or its first non-synthetic child.
  // This handles cases where the GLTF armature structure causes three.js
  // to omit the "root" bone from the skeleton bone list.
  if (!('Root' in chainMappings)) {
    const hasRootChain = rig.chains.some((c) => c.name === 'Root');
    if (hasRootChain) {
      const skelRoot = skel.bone(skel.root);
      const rootChild =
        skelRoot.auxiliary && skelRoot.children.length === 1
          ? skel.bone(skelRoot.children[0])
          : null;
      const rootBone = rootChild ?? skelRoot;
      const rootPresetChain = rig.chains.find((c) => c.name === 'Root');
      if (rootPresetChain) {
        const expectedName = rootPresetChain.startBone.toLowerCase();
        if (
          rootBone.name.toLowerCase() === expectedName ||
          rootBone.name.toLowerCase().includes(expectedName)
        ) {
          chainMappings['Root'] = [rootBone.index, rootBone.index, null];
        }
      }
    }
  }

  // Some presets intentionally describe only the authored body rig. When the
  // asset also contains a real animation root above the preset pelvis, keep it
  // by reusing the shared root detector rather than baking preset-specific
  // exceptions.
  maybeAddRootFromPresetPelvis(skel, chainMappings);

  for (const [chainName, [startIdx, endIdx, ikGoal]] of Object.entries(chainMappings)) {
    const chainBones: number[] = [];
    let current: number | null = endIdx;
    while (current !== null) {
      chainBones.unshift(current);
      if (current === startIdx) break;
      current = skel.bone(current).parent;
    }

    if (chainBones.length === 0 || chainBones[0] !== startIdx) {
      if (startIdx === endIdx) {
        chainBones.length = 0;
        chainBones.push(startIdx);
      } else {
        chainBones.length = 0;
        chainBones.push(startIdx, endIdx);
      }
    }

    const filteredBones: number[] = [];
    for (let i = 0; i < chainBones.length; i++) {
      const bone = skel.bone(chainBones[i]);
      const isEndpoint = i === 0 || i === chainBones.length - 1;
      if (isEndpoint || !bone.auxiliary) filteredBones.push(chainBones[i]);
    }

    const chainBoneNames = filteredBones.map((idx) => skel.refForBoneIndex(idx));
    chains[chainName] = chainBoneNames;

    chainResults[chainName] = new ChainMapping({
      name: chainName,
      startBone: skel.refForBoneIndex(startIdx),
      endBone: skel.refForBoneIndex(endIdx),
      boneNames: chainBoneNames,
      confidence: baseConfidence,
      ikGoal: ikGoal,
    });
  }

  const ctx: AugmentationContext = {
    skel,
    chains,
    chainResults,
    chainMappings,
    roleToBone,
    roleToConfidence,
    explain,
    baseConfidence,
    warnings,
    detectedFeatures: [],
    assignRole(role: string, boneRef: string) {
      this.roleToBone[role] = boneRef;
      this.roleToConfidence[role] = this.baseConfidence;
      this.explain[role] = makeBreakdown(this.baseConfidence);
    },
    boneIndex(ref: string) {
      return skel.resolveBoneRef(ref, { namespace: 'internal' });
    },
    boneName(idx: number) {
      return skel.refForBoneIndex(idx);
    },
    addFeature(label: string, description: string) {
      if (!this.detectedFeatures.some((f) => f.label === label)) {
        this.detectedFeatures.push({ label, description });
      }
    },
    log(_msg: string) {
      // no-op in browser
    },
  };

  runAllAugmentations(ctx);

  const mainBoneIndices = new Set<number>();
  for (const chainBones of Object.values(chains)) {
    for (const boneRef of chainBones) {
      const boneIdx = skel.resolveBoneRef(boneRef, { namespace: 'internal' });
      if (boneIdx !== null) mainBoneIndices.add(boneIdx);
    }
  }
  const accessoryExclusions = suggestAccessoryBranchExclusions(
    skel,
    mainBoneIndices,
  );
  for (const boneName of accessoryExclusions) {
    if (!excluded.includes(boneName)) excluded.push(boneName);
  }

  const toeChainsPresent = new Set(
    Object.keys(chainMappings).filter((n) => n.endsWith('Toe')),
  );

  for (const [chainName, [startIdx, endIdx, _ikGoal]] of Object.entries(chainMappings)) {
    const chainDef = CHAIN_DEFINITIONS[chainName];
    if (chainDef) {
      const startRole = chainDef.startRole;
      const endRole = chainDef.endRole;

      if (startRole && startIdx !== null) {
        roleToBone[startRole] = skel.refForBoneIndex(startIdx);
        roleToConfidence[startRole] = baseConfidence;
        explain[startRole] = makeBreakdown(baseConfidence);
      }

      if (endRole && endIdx !== null && endRole !== startRole) {
        const endBoneName = skel.bone(endIdx).name.toLowerCase();

        if (endRole.includes('Toe') && (chainName === 'LeftLeg' || chainName === 'RightLeg')) {
          const side = chainName.startsWith('Left') ? 'Left' : 'Right';
          const isFootBone = ['foot', 'ankle'].some((tok) => endBoneName.includes(tok));
          const isToeBone = ['toe', 'ball'].some((tok) => endBoneName.includes(tok));

          if (isFootBone && !isToeBone) {
            const footRole = `${side}Foot`;
            roleToBone[footRole] = skel.refForBoneIndex(endIdx);
            roleToConfidence[footRole] = baseConfidence;
            explain[footRole] = makeBreakdown(baseConfidence);
            continue;
          } else if (toeChainsPresent.has(`${side}Toe`)) {
            const footRole = `${side}Foot`;
            roleToBone[footRole] = skel.refForBoneIndex(endIdx);
            roleToConfidence[footRole] = baseConfidence;
            explain[footRole] = makeBreakdown(baseConfidence);
            continue;
          }
        }

        roleToBone[endRole] = skel.refForBoneIndex(endIdx);
        roleToConfidence[endRole] = baseConfidence;
        explain[endRole] = makeBreakdown(baseConfidence);
      }
    }

    if (chainName.endsWith('Toe') && !CHAIN_DEFINITIONS[chainName]) {
      const toeRole = chainName;
      roleToBone[toeRole] = skel.refForBoneIndex(startIdx);
      roleToConfidence[toeRole] = baseConfidence;
      explain[toeRole] = makeBreakdown(baseConfidence);
    }
  }

  function assignMiddleRole(role: string, boneName: string) {
    roleToBone[role] = boneName;
    roleToConfidence[role] = baseConfidence;
    explain[role] = makeBreakdown(baseConfidence);
  }

  const legJointHints = ['knee', 'calf', 'shin', 'lowerleg', 'tibia'];
  for (const side of ['Left', 'Right'] as const) {
    const legChainName = `${side}Leg`;
    if (!(legChainName in chains)) continue;

    const legBones = chains[legChainName];
    const legCr = chainResults[legChainName];
    const isDigitigrade = legCr && legCr.legType === 'digitigrade';

    if (isDigitigrade) continue;

    if (legBones.length >= 3) {
      const lowerLegRole = `${side}LowerLeg`;
      if (!(lowerLegRole in roleToBone)) {
        const jointBone = findJointBone(skel, legBones, legJointHints);
        assignMiddleRole(lowerLegRole, jointBone);
      }
    }
  }

  const armJointHints = ['elbow', 'forearm', 'lowerarm', 'radius', 'ulna'];
  for (const side of ['Left', 'Right'] as const) {
    const armChainName = `${side}Arm`;
    if (armChainName in chains) {
      const armBones = chains[armChainName];
      if (armBones.length >= 3) {
        const lowerArmRole = `${side}LowerArm`;
        if (!(lowerArmRole in roleToBone)) {
          const jointBone = findJointBone(skel, armBones, armJointHints);
          assignMiddleRole(lowerArmRole, jointBone);
        }
      }
    }
  }

  for (const side of ['Left', 'Right'] as const) {
    for (const fingerChainName of getFingerChains(side)) {
      if (fingerChainName in chains) {
        const fingerBones = chains[fingerChainName];
        for (const { boneIndex, roleName: role } of getChainRoleAssignments(
          fingerChainName,
          fingerBones.length,
        )) {
          const fingerBone = fingerBones[boneIndex];
          if (!fingerBone) continue;
          if (!(role in roleToBone)) {
            assignMiddleRole(role, fingerBone);
          }
        }
      }
    }
  }

  const presetDisplayName = detectedPresetName(preset);
  warnings.push(
    `Detected as ${presetDisplayName} skeleton (confidence: ${(baseConfidence * 100).toFixed(0)}%)`,
  );

  return new MappingResult({
    roleToBone,
    roleToConfidence,
    chains,
    chainResults,
    warnings,
    explain,
    presetName: presetDisplayName,
    presetId: preset.preset.id,
    detectedFeatures: ctx.detectedFeatures,
    excludedBones: excluded,
  });
}
